import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ButtonHTMLAttributes } from 'react';
import { BORDER_RADIUS, BORDER_WIDTH } from '../../styles/borders';
import { COLOR, GREYSCALE } from '../../styles/colors';
import { OPACITY } from '../../styles/opacities';
import { SPACING } from '../../styles/spacing';
import { TYPOGRAPHY } from '../../styles/typography';
import withOpacity from '../../utils/withOpacity';
import { getButtonSizeStyle, getButtonVariantStyle } from './buttonStyles';

export type ButtonVariant =
  | 'primary'
  | 'primaryOutline'
  | 'info'
  | 'success'
  | 'successOutline'
  | 'warning'
  | 'danger'
  | 'dark';
export type ButtonSize = 'small' | 'medium' | 'large' | 'xLarge';

// props needed for any elements that look like buttons (e.g. buttons, but also links in buttons' clothing)
export type ButtonStyleProps = {
  size?: ButtonSize;
  variant?: ButtonVariant;
  fullWidth?: boolean;
  noWrap?: boolean;
  additionalLabel?: string;
  hideForPrint?: boolean;
};

// props needed for our standard button
export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & ButtonStyleProps;

const Button = styled.button<ButtonProps>`
  display: inline-block;
  box-sizing: border-box;
  max-width: 100%;
  overflow: hidden;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : '')};
  margin: ${SPACING.none};
  border: ${BORDER_WIDTH.sm} solid transparent;
  text-align: center;
  vertical-align: middle;
  white-space: ${({ noWrap }) => (noWrap ? 'nowrap' : 'normal')};
  text-overflow: ellipsis;
  user-select: none;
  cursor: pointer;
  @media print {
    display: ${({ hideForPrint }) => (hideForPrint ? 'none' : 'inline')};
  }
  :focus-within {
    outline-color: ${COLOR.blue};
  }

  /* Button sizes */
  ${({ size }) => getButtonSizeStyle(size)}

  /* Button variants */
  ${({ variant }) => getButtonVariantStyle(variant)}

  :active {
    box-shadow: inset ${SPACING.none} ${SPACING.xs} ${SPACING.sm}
      ${withOpacity(GREYSCALE.black, 0.125)};
  }

  :disabled {
    opacity: ${OPACITY.disabled};
    cursor: not-allowed;
  }

  ${({ additionalLabel }) => {
    if (additionalLabel) {
      return css`
        ::after {
          content: '${additionalLabel}';
          position: absolute;
          top: -${SPACING.md};
          right: ${SPACING.xl};
          padding: ${SPACING.xs} ${SPACING.sm};
          border-radius: ${BORDER_RADIUS.xxs};
          background: ${GREYSCALE.black};
          color: ${GREYSCALE.white};
          text-transform: uppercase;
          font-size: ${TYPOGRAPHY.fontSize.xs};
          font-weight: ${TYPOGRAPHY.fontWeight.medium};
        }
      `;
    }

    return undefined;
  }}
`;

export default Button;
